/**
 * Calendar — `activities.html#calendar` ported, and made to actually work.
 *
 * The prototype's month grid has no click handlers at all — not on a day, not
 * on an event chip, not even on its own Day/Week/Month segmented control or
 * its prev/next chevrons (those are just markup). This wires the whole thing
 * to the real `GET /activities/calendar` window query (§8) instead of the
 * fixture-shaped `crm.api.js` the rest of `ActivitiesPages.jsx` still reads,
 * and adds the interactions a calendar actually needs: open a day, open an
 * event, edit or complete it. Mark complete is disabled (greyed out, with a
 * tooltip) until the event's own scheduled time has passed — completing
 * something the calendar still shows as upcoming would be recording an
 * outcome that hasn't happened. Edit has no such restriction: a not-yet-due
 * event is exactly the one someone needs to reschedule. Built by hand rather
 * than with a calendar library, for the same
 * reason the screen it replaces was — a full scheduling component brings
 * drag-to-reschedule and timezone handling nothing here asks for.
 *
 * No Day/Week toggle: `calendar()` on the backend takes an arbitrary
 * `from`/`to` window (capped at 92 days), so a week view is only a narrower
 * request, not a different endpoint — worth adding once someone asks for it,
 * not before.
 *
 * Not a routed page any more — `/calendar` used to point straight here. It's
 * the same activity rows as `ActivitiesPage.jsx`, a month grid instead of a
 * table, so it's now that screen's own List/Calendar toggle instead of a
 * second nav entry a click away that just came back here (same move already
 * made for Pipeline/Opportunities). `/calendar` redirects to
 * `/activities?view=calendar` for anyone with it bookmarked. No `PageHeader`
 * here any more either, since embedding one would put two `<h1>`s on the
 * page — the month name and its nav buttons live in a `Card` instead.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth.js';
import {
  Card, Drawer, ErrorState, MockBadge,
} from '../../components/common/index.jsx';
import { PageLoader } from '../../components/common/Loading.jsx';
import { isMock } from '../../api/mockFallback.js';
import { date, time, dateTime } from '../../utils/formatters.js';
import { queryKeys } from '../../app/queryClient.js';
import * as usersApi from '../../api/users.api.js';
import { useActivitiesCalendar, useCompleteActivity } from './useActivities.js';
import {
  KIND_META, ActivityDetail, EditActivityModal, isDueOrPast,
} from './ActivityTimeline.jsx';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ALL_KINDS = Object.keys(KIND_META);

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const sameDay = (a, b) => a.toDateString() === b.toDateString();

/** The 6×7 grid start — Monday of the week containing the 1st. Always six
 * rows, so paging from a 4-week month into a 5-week one does not jump the
 * page height. */
function gridStart(monthStart) {
  const offset = (monthStart.getDay() + 6) % 7;
  const start = new Date(monthStart);
  start.setDate(monthStart.getDate() - offset);
  return start;
}

export default function CalendarView() {
  const { hasScope, can } = useAuth();
  /** `calendar:view` only gates the endpoint; row visibility is decided by the
   * caller's `activities` scope (RLS reads that screen, not `calendar` — see
   * activities.api.js's getCalendar doc comment). Below `team` there, a
   * teammate's user_id would just come back empty, so don't offer the picker. */
  const canFilterByOwner = hasScope('activities', 'team');
  const canComplete = can('activities', 'edit');

  const [monthStart, setMonthStart] = useState(() => startOfMonth(new Date()));
  const [activeKinds, setActiveKinds] = useState(() => new Set(ALL_KINDS));
  const [ownerId, setOwnerId] = useState('');
  const [drawer, setDrawer] = useState(null); // { mode: 'day', date } | { mode: 'event', event }
  const [editTarget, setEditTarget] = useState(null);

  const start = useMemo(() => gridStart(monthStart), [monthStart]);
  const end = useMemo(() => {
    const d = new Date(start);
    d.setDate(d.getDate() + 42);
    return d;
  }, [start]);

  const params = useMemo(() => ({
    from: start.toISOString(),
    to: end.toISOString(),
    user_ids: ownerId || undefined,
  }), [start, end, ownerId]);

  const { data, isLoading, error, refetch } = useActivitiesCalendar(params);

  const usersQuery = useQuery({
    queryKey: queryKeys.users.list({ limit: 200, picker: true }),
    queryFn: () => usersApi.listUsers({ limit: 200 }),
    enabled: canFilterByOwner,
    staleTime: 300_000,
  });
  const owners = (usersQuery.data?.data ?? []).map((u) => ({ id: u.id, name: u.full_name ?? u.email }));

  const events = useMemo(
    () => (data?.data ?? []).filter((a) => a.scheduled_start && activeKinds.has(a.kind)),
    [data, activeKinds],
  );

  const weeks = useMemo(() => Array.from({ length: 6 }, (_, week) => Array.from({ length: 7 }, (_, day) => {
    const cellDate = new Date(start);
    cellDate.setDate(start.getDate() + week * 7 + day);
    const items = events
      .filter((a) => sameDay(new Date(a.scheduled_start), cellDate))
      .sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
    return {
      date: cellDate,
      inMonth: cellDate.getMonth() === monthStart.getMonth(),
      isToday: sameDay(cellDate, new Date()),
      items,
    };
  })), [start, events, monthStart]);

  const dayCell = (d) => weeks.flat().find((cell) => sameDay(cell.date, d));

  const shiftMonth = (delta) => setMonthStart((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  const toggleKind = (kind) => setActiveKinds((prev) => {
    const next = new Set(prev);
    if (next.has(kind)) next.delete(kind); else next.add(kind);
    return next;
  });

  const completeMutation = useCompleteActivity();

  if (error) return <ErrorState error={error} onRetry={refetch} />;

  return (
    <>
      <Card
        className="mb-3"
        title={(
          <>
            {monthStart.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            {isMock(data) && <MockBadge section="activities" />}
          </>
        )}
        actions={(
          <>
            <button type="button" className="btn-x btn-ghost" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <i className="bi bi-chevron-left" aria-hidden="true" />
            </button>
            <button type="button" className="btn-x btn-ghost" onClick={() => setMonthStart(startOfMonth(new Date()))}>
              Today
            </button>
            <button type="button" className="btn-x btn-ghost" onClick={() => shiftMonth(1)} aria-label="Next month">
              <i className="bi bi-chevron-right" aria-hidden="true" />
            </button>
          </>
        )}
      >
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div className="cal-legend">
            {ALL_KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                className={`cal-legend-item ${activeKinds.has(kind) ? '' : 'is-off'}`}
                style={{ '--dot': KIND_META[kind][1] }}
                onClick={() => toggleKind(kind)}
                title={activeKinds.has(kind) ? `Hide ${kind}` : `Show ${kind}`}
              >
                <span className="cal-dot" />{kind}
              </button>
            ))}
          </div>

          {canFilterByOwner && (
            <select
              className="form-select form-select-sm w-auto"
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
            >
              <option value="">My calendar</option>
              {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
        </div>

        {isLoading ? (
          <PageLoader label="Loading calendar" />
        ) : (
          <div className="cal-card fade-up">
            <div className="cal-head-row">
              {WEEKDAYS.map((d) => <span key={d}>{d}</span>)}
            </div>
            <div className="cal-grid">
              {weeks.flat().map((cell) => (
                <div
                  key={cell.date.toISOString()}
                  className={`cal-cell ${cell.inMonth ? '' : 'dim'} ${cell.isToday ? 'today' : ''}`}
                >
                  <button type="button" className="cal-date" onClick={() => setDrawer({ mode: 'day', date: cell.date })}>
                    {cell.date.getDate()}
                  </button>
                  {cell.items.slice(0, 3).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="cal-ev"
                      style={{ '--tint': KIND_META[item.kind]?.[1] ?? 'var(--ink-3)' }}
                      title={`${time(item.scheduled_start)} ${item.subject}`}
                      onClick={() => setDrawer({ mode: 'event', event: item })}
                    >
                      <i className={`bi ${KIND_META[item.kind]?.[0] ?? 'bi-circle'}`} aria-hidden="true" />
                      {item.subject}
                    </button>
                  ))}
                  {cell.items.length > 3 && (
                    <button type="button" className="cal-more" onClick={() => setDrawer({ mode: 'day', date: cell.date })}>
                      +{cell.items.length - 3} more
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Drawer
        open={Boolean(drawer)}
        onClose={() => setDrawer(null)}
        title={drawer?.mode === 'event' ? drawer.event.subject : drawer && date(drawer.date)}
        subtitle={drawer?.mode === 'day'
          ? `${dayCell(drawer.date)?.items.length ?? 0} scheduled`
          : drawer?.mode === 'event' ? drawer.event.all_day ? 'All day' : dateTime(drawer.event.scheduled_start) : undefined}
      >
        {drawer?.mode === 'day' && (
          <DayAgenda items={dayCell(drawer.date)?.items ?? []} onOpen={(event) => setDrawer({ mode: 'event', event })} />
        )}
        {drawer?.mode === 'event' && (
          <ActivityDetail
            activity={drawer.event}
            canComplete={canComplete}
            canEdit={canComplete}
            busy={completeMutation.isPending}
            completeDisabledReason={isDueOrPast(drawer.event) ? undefined : "Can't complete an event that hasn't happened yet."}
            onComplete={() => completeMutation.mutate(
              { id: drawer.event.id },
              { onSuccess: () => setDrawer(null) },
            )}
            onEdit={() => setEditTarget(drawer.event)}
          />
        )}
      </Drawer>

      <EditActivityModal
        activity={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { setEditTarget(null); setDrawer(null); }}
      />
    </>
  );
}

function DayAgenda({ items, onOpen }) {
  if (!items.length) return <p className="t-sm text-muted mb-0">Nothing scheduled.</p>;
  return (
    <div>
      {items.map((item) => {
        const [icon, color] = KIND_META[item.kind] ?? ['bi-circle', 'var(--ink-3)'];
        return (
          <button key={item.id} type="button" className="cal-agenda-item" onClick={() => onOpen(item)}>
            <span className="cal-agenda-time">{item.all_day ? 'All day' : time(item.scheduled_start)}</span>
            <i className={`bi ${icon}`} style={{ color }} aria-hidden="true" />
            <span className="min-w-0">
              <span className="d-block t-sm fw-650">{item.subject}</span>
              {item.owner && <span className="d-block t-2xs text-muted">{item.owner.full_name}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
